<template>
  <div class="ai-option-dropdown" :style="positionStyle">
    <b-dropdown
      ref="aiDropdown"
      :visible="true"
      no-caret
      no-flip
      right
      menu-class="ai-option-menu p-gd-1"
      toggle-class="d-none"
      @hide.prevent
    >
      <!-- AI Options List -->
      <b-dropdown-item-button
        v-for="(option, index) in options"
        :key="index"
        button-class="ai-option-item d-flex align-items-center fs-sa-14 fw-400 p-gd-1 cursor-pointer border-none w-100 text-left"
        :active="selectedOption === index"
        :class="{ 'has-submenu-open': option.hasSubmenu && openSubmenuIndex === index }"
        @click="handleOptionClick(option, index, $event)"
        @mouseenter.native="handleOptionHover(option, index)"
        @mouseleave.native="handleOptionLeave"
      >
        <div class="ai-option-icon d-flex align-items-center justify-content-center flex-shrink-0 mr-8">
          <img :src="option.icon" class="ai-option-svg" style="width: 16px; height: 16px;">
        </div>
        <div class="flex-fill fs-sa-14 fw-400 text-default">{{ $t(option.label, languageEngine) }}</div>
        <div v-if="option.hasSubmenu" class="flex-shrink-0">
          <i class="fal fa-chevron-right fs-sa-12 fw-400 text-hint ml-auto"></i>
        </div>

        <!-- Submenu -->
        <div
          v-if="option.hasSubmenu && getSubmenuItems(option).length && openSubmenuIndex === index"
          class="ai-submenu bg-white"
          @mouseenter="handleSubmenuEnter"
          @mouseleave="handleSubmenuLeave"
        >
          <b-dropdown-item-button
            v-for="(subOption, subIndex) in getSubmenuItems(option)"
            :key="subIndex"
            button-class="ai-submenu-item cursor-pointer border-none w-100 text-left"
            @click="handleSubmenuClick(subOption, option, $event)"
          >
            <span class="d-flex align-items-center fs-sa-14 fw-400 text-dark">
              <i v-if="subOption.flagIcon" :class="`flag-icon flag-icon-${subOption.flagIcon} mr-8`"></i>
              {{  ['translate', 'tone'].includes(subOption.id) ? subOption.label : $t(subOption.label, languageEngine) }}
            </span>
          </b-dropdown-item-button>
        </div>
      </b-dropdown-item-button>

      <!-- Credit Information -->
      <b-dropdown-text text-class="ai-option-footer d-flex align-items-center flex-wrap p-gd-1">
        <b-skeleton-wrapper :loading="isLoadingAiCredit" :row="1" :col="1">
          <template #loading>
            <b-skeleton height="16px" width="190px"></b-skeleton>
          </template>
          <template #default>
            <div class="d-flex justify-content-between w-100 flex-wrap align-items-center gap-gd-1">
              <span class="fs-sa-12 text-hint fw-400">{{ $t('engine.aiOption.footer.lblRemainingCredits', languageEngine) }}</span>
              <AIGenerateCreditDisplay />
            </div>
          </template>
        </b-skeleton-wrapper>
      </b-dropdown-text>
    </b-dropdown>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AIGenerateCreditDisplay from '@components_system/Design/AIGenerate/AIGenerateCreditDisplay.vue'; 
export default {
  name: 'AIOption',
  components: {
    AIGenerateCreditDisplay
  },
  props: {
    position: {
      type: Object,
      default: () => ({ top: '0px', left: '0px', right: '0px' })
    },
    options: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selectedOption: 0,
      hoveredOption: null,
      openSubmenuIndex: null,
      keepSubmenuOpen: false,
      closeSubmenuTimer: null,
    };
  },
  created() {
    if(!this.creditDetailAI || (this.creditDetailAI && this.creditDetailAI.available === undefined)) {
      this.$store.dispatch('designIframe/credit/init', { type: 'ai' }, { root: true });
    }
  },
  beforeDestroy() {
    this.clearCloseSubmenuTimer();
  },
  computed: {
    ...mapGetters({
      languages: 'designIframe/page/languages',
      languageEngine: 'designIframe/userPreference/languageEngine',
      creditDetailAI: 'designIframe/credit/creditDetailAI',
      isLoadingAiCredit: 'designIframe/credit/isLoadingAiCredit'
    }),
    translateSubmenu() {
      if (!this.languages) return [];
      return Object.keys(this.languages).map(key => ({
        id: key,
        label: this.languages[key].language.toUpperCase(),
        flagIcon: this.languages[key].flagIcon
      }));
    },
    positionStyle() {
      return {
        top: this.position.top,
        left: this.position.left,
        right: this.position.right
      };
    },
  },
  methods: {
    getSubmenuItems(option) {
      if (option.id === 'translate') {
        return this.translateSubmenu;
      }
      return option.submenu || [];
    },
    handleOptionClick(option, index, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.selectedOption = index;

      if (option.hasSubmenu) {
        if (this.openSubmenuIndex === index) {
          this.openSubmenuIndex = null;
        } else {
          this.openSubmenuIndex = index;
        }
      } else {
        this.$emit('option-click', option);
      }
    },
    clearCloseSubmenuTimer() {
      if (this.closeSubmenuTimer) {
        clearTimeout(this.closeSubmenuTimer);
        this.closeSubmenuTimer = null;
      }
    },
    handleOptionHover(option, index) {
      this.hoveredOption = index;
      this.clearCloseSubmenuTimer();

      if (option.hasSubmenu) {
        this.openSubmenuIndex = index;
      } else {
        this.openSubmenuIndex = null;
        this.keepSubmenuOpen = false;
      }
    },
    handleOptionLeave() {
      this.hoveredOption = null;
      this.clearCloseSubmenuTimer();
      this.closeSubmenuTimer = setTimeout(() => {
        if (!this.keepSubmenuOpen) {
          this.openSubmenuIndex = null;
        }
        this.closeSubmenuTimer = null;
      }, 100);
    },
    handleSubmenuEnter() {
      this.clearCloseSubmenuTimer();
      this.keepSubmenuOpen = true;
    },
    handleSubmenuLeave() {
      this.keepSubmenuOpen = false;
      this.clearCloseSubmenuTimer();
      this.closeSubmenuTimer = setTimeout(() => {
        if (!this.keepSubmenuOpen) {
          this.openSubmenuIndex = null;
        }
        this.closeSubmenuTimer = null;
      }, 100);
    },
    handleSubmenuClick(subOption, parentOption, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.$emit('submenu-click', {
        parent: parentOption,
        subOption: subOption
      });

      this.openSubmenuIndex = null;
      this.keepSubmenuOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-option-dropdown {
  position: absolute;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: all;

  ::v-deep .dropdown {
    position: static;
  }

  ::v-deep .ai-option-menu {
    display: block !important;
    position: static !important;
    float: none;
    border-radius: 16px;
    box-shadow: 0 2px 10px 0 rgba(0, 53, 74, 0.15) !important;
    width: 276px;
    overflow: visible;
    pointer-events: all;
    border: none;
    transform: none !important;

    > li {
      position: relative;
      list-style: none;

      &.has-submenu-open > .ai-option-item {
        background-color: #EBFAFF;
        border-radius: 8px;

        .ai-option-icon .ai-option-svg {
          filter: brightness(0) saturate(100%) invert(36%) sepia(72%) saturate(1060%) hue-rotate(163deg) brightness(92%) contrast(93%);
        }

        .flex-fill {
          color: #0C739D !important;
          font-weight: 500;
        }
      }
    }

    .ai-option-item {
      transition: background-color 0.2s;
      background-color: transparent;

      &:hover,
      &.dropdown-item:hover {
        background-color: #EBFAFF;
        border-radius: 8px;

        .ai-option-icon .ai-option-svg {
          filter: brightness(0) saturate(100%) invert(36%) sepia(72%) saturate(1060%) hue-rotate(163deg) brightness(92%) contrast(93%);
        }

        .flex-fill {
          color: #0C739D !important;
          font-weight: 500;
        }
      }

      &:focus,
      &.dropdown-item:focus {
        outline: none;
        box-shadow: none;
        background-color: transparent;
      }

      &:active,
      &.dropdown-item:active {
        background-color: #EBFAFF;
        color: inherit;

        .ai-option-icon .ai-option-svg {
          filter: brightness(0) saturate(100%) invert(36%) sepia(72%) saturate(1060%) hue-rotate(163deg) brightness(92%) contrast(93%);
        }

        .flex-fill {
          color: #0C739D !important;
          font-weight: 500;
        }
      }
    }

    .active > .ai-option-item,
    .active > button.dropdown-item {
      background-color: #EBFAFF;
      border-radius: 8px;
      color: inherit;

      .ai-option-icon .ai-option-svg {
        filter: brightness(0) saturate(100%) invert(36%) sepia(72%) saturate(1060%) hue-rotate(163deg) brightness(92%) contrast(93%);
      }

      .flex-fill {
        color: #0C739D !important;
        font-weight: 500;
      }
    }

    .ai-option-icon {
      width: 16px;
      height: 16px;

      .ai-option-svg {
        object-fit: contain;
        transition: filter 0.2s;
      }
    }

    .ai-submenu {
      position: absolute;
      left: calc(100% + 4px);
      top: 0;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      min-width: 200px;
      z-index: 1001;
      padding: 8px 0;
      overflow: visible;
      animation: slideInRight 0.2s ease-out;
      pointer-events: all;

      li {
        list-style: none;
      }

      .ai-submenu-item {
        padding: 12px 16px;
        transition: background-color 0.2s;
        background-color: transparent;
        pointer-events: all;
        user-select: none;

        &:hover,
        &.dropdown-item:hover {
          background-color: #EBFAFF;
        }

        &:active,
        &.dropdown-item:active {
          background-color: #E0E0E0;
          color: inherit;
        }

        &:focus,
        &.dropdown-item:focus {
          outline: none;
          box-shadow: none;
          background-color: transparent;
        }
      }
    }

    .dropdown-divider {
      margin: 0;
      border-color: #E0E0E0;
    }

    .ai-option-footer {
      .credit-separator {
        width: 1px;
        height: 16px;
        background-color: #DFDFDF;
      }

      .usage-amount {
        color: #E13023;
      }
    }
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
